// A queued follow-up must stay ineligible for message approval across failed
// sends, retries and reloads. Its origin belongs to the durable user message,
// not to a one-shot flag consumed before the transport reaches the server.
import type { CompleteAttachment, CreateAppendMessage } from '@assistant-ui/react';
import type { UIMessage } from 'ai';
import type { SteerMessage } from './parts';

/** The invisible data part survives the native SDK and the history mirror. */
export function queuedTurnMessage(
  text: string,
  attachments: readonly CompleteAttachment[] = [],
  steerIds: readonly string[] = [],
): CreateAppendMessage {
  return {
    role: 'user',
    attachments,
    content: [
      { type: 'text', text },
      { type: 'data', name: 'steerQueued', data: { origin: 'queued', steerIds } },
    ],
  };
}

/** A saved queued turn retires the unread records that supplied its message. */
export function queuedSteerIds(message: SteerMessage): string[] {
  if (message.role !== 'user') return [];
  return message.content.flatMap((part) => {
    if (part.type !== 'data' || part.name !== 'steerQueued') return [];
    const data = part.data as { origin?: unknown; steerIds?: unknown } | null;
    return data?.origin === 'queued' && Array.isArray(data.steerIds)
      ? data.steerIds.filter((id): id is string => typeof id === 'string')
      : [];
  });
}

/** Only the submitted message can supply origin; an older queued turn cannot. */
export function queuedOriginBody(message: UIMessage | undefined): { origin: 'queued' } | null {
  if (message?.role !== 'user') return null;
  return message.parts.some(
    (part) =>
      part.type === 'data-steerQueued' &&
      (part.data as { origin?: unknown } | null)?.origin === 'queued',
  )
    ? { origin: 'queued' }
    : null;
}
